import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useReverseTransaction } from '../../../services/loanService';
import type { LedgerTransaction } from '../../../services/schemas/loanServiceSchemas/ledgerTransaction';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { toLocalDateString } from '../../../utils/localDate';

interface Props {
  discount: LedgerTransaction | null;
  onHide: () => void;
  /** Called after the reversal is posted, so the page can reload the balance and ledger. */
  onReversed?: () => void;
}

/**
 * Undo a discount captured in error. A reversing entry is posted against it (the original stays on
 * the ledger), the amount is owed again, and a loan the discount had closed reopens.
 */
const ReverseDiscountModal: React.FC<Props> = ({ discount, onHide, onReversed }) => {
  const reverse = useReverseTransaction();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (discount) setReason('');
  }, [discount]);

  const submit = async () => {
    if (!discount?.txnId || !reason.trim()) return;
    try {
      await reverse.mutateAsync({
        data: {
          originalTxnId: discount.txnId,
          valueDate: toLocalDateString(),
          idempotencyKey: `reverse-${discount.txnId}`,
          remarks: reason.trim(),
        }
      });
      toast.success('Discount reversed');
      onReversed?.();
      onHide();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to reverse the discount');
    }
  };

  return (
    <Modal show={!!discount} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Reverse discount</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {discount && (
          <div className="p-3 mb-3 bg-light rounded small">
            <div><strong>{formatCurrency(Number(discount.amountDec || 0))}</strong> on {formatDate(discount.valueDate || '')}</div>
            {discount.remarks && <div className="text-muted">{discount.remarks}</div>}
          </div>
        )}
        <p className="text-muted small">
          The amount becomes owed again. The discount stays on the ledger with a reversing entry against it.
        </p>
        <Form.Group>
          <Form.Label>Reason <span className="text-danger">*</span></Form.Label>
          <Form.Control as="textarea" rows={2} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. Captured on the wrong account" />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="danger" onClick={submit} disabled={!reason.trim() || reverse.isPending}>
          {reverse.isPending ? 'Reversing...' : 'Reverse discount'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReverseDiscountModal;
